import { actions, feedbackV2, inputsV2, overlays } from '@ecosystem/newron-design-system';
import React, { useMemo, useState } from 'react';
import { AccessLevel, AvailableShareUser, ShareUser } from './types';
import { mapAvailableShareUserToSelectOption } from './utils';

interface ModalProps {
  isOpen: boolean;
  onCancel: () => void;
  onConfirm: (newOwner: ShareUser) => void;
  itemType: string;
  availableUsers: AvailableShareUser[];
}
const { Modal } = overlays;
const { Button } = actions;
const { Alert } = feedbackV2;
const { Select } = inputsV2;

export const OwnershipTransferModal = ({
  itemType,
  isOpen,
  onCancel,
  onConfirm,
  availableUsers,
}: ModalProps): JSX.Element => {
  const [selectedUser, setSelectedUser] = useState<AvailableShareUser | null>(null);

  const usersOptions = useMemo(() => {
    return (availableUsers ?? []).map(mapAvailableShareUserToSelectOption);
  }, [availableUsers]);

  const handleSelectUser = (userId?: string) => {
    const user = availableUsers.find(user => user.userId === userId);
    setSelectedUser(user);
  };

  const handleConfirm = () => {
    const newOwner = { ...selectedUser, accessLevel: AccessLevel.Owner };
    onConfirm(newOwner);
  };

  const handleCancel = () => {
    setSelectedUser(null);
    onCancel();
  };

  return (
    <Modal isOpen={isOpen} onClose={onCancel} renderInPortal={true}>
      <Modal.Header>Change {itemType} owner</Modal.Header>
      <Modal.Body>
        <div data-test-id="ownership-transfer-select" className="pt-4">
          <Select
            label="New owner"
            disabled={usersOptions.length === 0}
            name="ownership-transfer-modal-select"
            placeholder="Search and select a user"
            options={usersOptions}
            onChange={handleSelectUser}
            informationMessage="No matching users found."
            combobox
          />
        </div>
        <div className="mt-6">
          <Alert
            title="Once completed, this action cannot be undone. Only the new owner can transfer ownership back."
            type="warn"
          />
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button onClick={handleCancel} mode="secondary" testId="cancel-btn">
          Go back
        </Button>
        <Button disabled={!selectedUser} onClick={handleConfirm} mode="primary" testId="confirm-btn">
          Change
        </Button>
      </Modal.Footer>
    </Modal>
  );
};
